import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Skeleton,
  Button,
  Chip,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { financeApi } from '../../api/financeApi';
import { formatCurrency } from '../../config/currency';
import { useCountryConfig } from '../../hooks/useCountryConfig';
import FinanceTabBar from '../../components/finance/FinanceTabBar';
import FinanceDateRange from '../../components/finance/FinanceDateRange';
import { PrimaryButton, SecondaryButton, PageHeroBand, EmptyState } from '../../components/ui';
import ExpenseCategoryPie from '../../components/finance/ExpenseCategoryPie';
import FoodCostGauge from '../../components/finance/FoodCostGauge';
import ComparisonBadge from '../../components/finance/ComparisonBadge';
import OnboardingWizard from '../../components/finance/OnboardingWizard';
import CollapsibleChartSection from '../../components/finance/CollapsibleChartSection';
import IncomeExpenseBarChart from '../../components/finance/IncomeExpenseBarChart';
import ChannelStackedBarChart from '../../components/finance/ChannelStackedBarChart';
import TopPayeesChart from '../../components/finance/TopPayeesChart';
import DayOfWeekHeatmap from '../../components/finance/DayOfWeekHeatmap';
import CashflowWaterfall from '../../components/finance/CashflowWaterfall';
import KpiSparkline from '../../components/finance/KpiSparkline';
import InsightsList from '../../components/finance/InsightsList';
import QuickActions from '../../components/finance/QuickActions';

const ONBOARDING_KEY = 'finance_onboarding_completed';
const INCLUDE_UNRECORDED_KEY = 'fd_include_unrecorded';
const SHOW_UNRECORDED_FEATURE = false;
import { downloadBlob } from '../../utils/downloadBlob';
import { thisMonthRange, lastMonthRange, todayLocalIso, toLocalIsoDate } from '../../utils/dateLocal';
import { useFilterParams } from '../../hooks/useFilterParams';

// --- Types ---

type PeriodKey = 'TODAY' | 'THIS_WEEK' | 'THIS_MONTH' | 'LAST_MONTH' | 'CUSTOM';

interface ComparisonData {
  previousIncome?: number;
  previousExpenses?: number;
  previousNetProfit?: number;
  previousFoodCostPercent?: number;
}

interface BudgetProgress {
  categoryName: string;
  budgetAmount: number;
  spentAmount: number;
  percent: number;
}

interface TaxSummary {
  cgst: number;
  sgst: number;
  igst: number;
}

interface DashboardData {
  totalIncome: number;
  totalExpenses: number;
  nonRevenueCosts: number;
  netProfit: number;
  unrecordedRevenue: number;
  foodCostPercent: number;
  foodCostTarget: number;
  pnlChart: Array<{ date: string; income: number; expenses: number; unrecordedRevenue?: number }>;
  expensesByCategory: Array<{ category: string; amount: number; percent: number }>;
  incomeByCategory: Array<{ category: string; amount: number; percent: number }>;
  payablesSummary: {
    totalOutstanding: number;
    overdueAmount: number;
    topSuppliers: Array<{ name: string; outstanding: number }>;
  };
  comparison?: ComparisonData;
  budgetProgress?: BudgetProgress[];
  taxSummary?: TaxSummary;
  fixedCosts?: number;
  expectedDeposits?: { count: number; total: number };
}

const EMPTY_DASHBOARD: DashboardData = {
  totalIncome: 0,
  totalExpenses: 0,
  nonRevenueCosts: 0,
  netProfit: 0,
  unrecordedRevenue: 0,
  foodCostPercent: 0,
  foodCostTarget: 30,
  pnlChart: [],
  expensesByCategory: [],
  incomeByCategory: [],
  payablesSummary: { totalOutstanding: 0, overdueAmount: 0, topSuppliers: [] },
};

const PERIOD_OPTIONS: { key: PeriodKey; label: string }[] = [
  { key: 'TODAY', label: 'Today' },
  { key: 'THIS_WEEK', label: 'This Week' },
  { key: 'THIS_MONTH', label: 'This Month' },
  { key: 'LAST_MONTH', label: 'Last Month' },
  { key: 'CUSTOM', label: 'Custom' },
];

function getDateRange(period: PeriodKey): { dateFrom: string; dateTo: string } {
  const now = new Date();
  const fmt = toLocalIsoDate;

  switch (period) {
    case 'TODAY':
      return { dateFrom: fmt(now), dateTo: fmt(now) };
    case 'THIS_WEEK': {
      const day = now.getDay();
      const monday = new Date(now);
      monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
      return { dateFrom: fmt(monday), dateTo: fmt(now) };
    }
    case 'THIS_MONTH':
      return thisMonthRange();
    case 'LAST_MONTH':
      return lastMonthRange();
    default:
      return { dateFrom: fmt(now), dateTo: fmt(now) };
  }
}

// --- Component ---

const FinanceDashboard: React.FC = () => {
  const { theme } = useTheme();
  const { taxSublabels } = useCountryConfig();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashFilters, setDashFilters] = useFilterParams(
    { period: 'THIS_MONTH', customFrom: '', customTo: '' },
    { prefix: 'fd_' },
  );
  const period = dashFilters.period as PeriodKey;
  const customFrom = dashFilters.customFrom;
  const customTo = dashFilters.customTo;
  const setPeriod = useCallback(
    (v: PeriodKey) => setDashFilters({ period: v }),
    [setDashFilters],
  );
  const setCustomFrom = useCallback(
    (v: string) => setDashFilters({ customFrom: v }),
    [setDashFilters],
  );
  const setCustomTo = useCallback(
    (v: string) => setDashFilters({ customTo: v }),
    [setDashFilters],
  );
  const [data, setData] = useState<DashboardData>(EMPTY_DASHBOARD);
  const [activeDateFrom, setActiveDateFrom] = useState('');
  const [activeDateTo, setActiveDateTo] = useState('');
  const [hasData, setHasData] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [includeUnrecorded, setIncludeUnrecorded] = useState<boolean>(() => {
    const stored = localStorage.getItem(INCLUDE_UNRECORDED_KEY);
    return stored === null ? false : stored === 'true';
  });
  useEffect(() => {
    localStorage.setItem(INCLUDE_UNRECORDED_KEY, String(includeUnrecorded));
  }, [includeUnrecorded]);

  const fetchDashboard = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const range = period === 'CUSTOM'
        ? { dateFrom: customFrom, dateTo: customTo }
        : getDateRange(period);

      if (period === 'CUSTOM' && (!customFrom || !customTo)) {
        if (showRefresh) setRefreshing(false);
        return;
      }

      setActiveDateFrom(range.dateFrom);
      setActiveDateTo(range.dateTo);
      const res = await financeApi.getDashboard({ ...range, includeComparison: true });
      const d = res.data || EMPTY_DASHBOARD;
      setData({
        totalIncome: d.totalIncome ?? 0,
        totalExpenses: d.totalExpenses ?? 0,
        nonRevenueCosts: d.nonRevenueCosts ?? 0,
        netProfit: d.netProfit ?? (d.totalIncome ?? 0) - (d.totalExpenses ?? 0) - (d.nonRevenueCosts ?? 0),
        unrecordedRevenue: d.unrecordedRevenue ?? 0,
        foodCostPercent: d.foodCostPercent ?? 0,
        foodCostTarget: d.foodCostTarget ?? 30,
        pnlChart: d.pnlChart ?? [],
        expensesByCategory: d.expensesByCategory ?? [],
        incomeByCategory: d.incomeByCategory ?? [],
        payablesSummary: d.payablesSummary ?? EMPTY_DASHBOARD.payablesSummary,
        comparison: d.comparison ?? undefined,
        budgetProgress: d.budgetProgress ?? [],
        taxSummary: d.taxSummary ?? undefined,
        fixedCosts: d.fixedCosts ?? 0,
        expectedDeposits: d.expectedDeposits ?? undefined,
      });
      const dataExists =
        (d.totalIncome ?? 0) > 0 ||
        (d.totalExpenses ?? 0) > 0 ||
        (d.pnlChart ?? []).length > 0;
      setHasData(dataExists);

      // Show onboarding wizard if no data and not previously completed
      const onboardingDone = localStorage.getItem(ONBOARDING_KEY);
      if (!dataExists && !onboardingDone) {
        setShowOnboarding(true);
      }
    } catch (err: unknown) {
      console.error('Error fetching dashboard:', err);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period, customFrom, customTo]);

  useEffect(() => {
    setLoading(true);
    fetchDashboard();
  }, [fetchDashboard]);

  const handleRefresh = () => fetchDashboard(true);

  const handleExportPnl = async () => {
    setExporting(true);
    const toastId = toast.loading('Exporting...');
    try {
      const range = period === 'CUSTOM'
        ? { dateFrom: customFrom, dateTo: customTo }
        : getDateRange(period);
      const params: Record<string, unknown> = { format: 'CSV', ...range };
      const res = await financeApi.exportData('pnl', params);
      const filename = `pnl-report-${todayLocalIso()}.csv`;
      downloadBlob(res.data, filename);
      toast.success('Downloaded!', { id: toastId });
    } catch (err: unknown) {
      console.error('Export failed:', err);
      toast.error('Export failed', { id: toastId });
    } finally {
      setExporting(false);
    }
  };

  // --- Shared Styles ---

  const cardSx = {
    p: { xs: 1.5, sm: 2 },
    borderRadius: '12px',
    backgroundColor: theme.components?.card?.background || '#FFFFFF',
    border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
  };

  const sectionTitle = (text: string) => (
    <Typography
      sx={{
        fontWeight: 700,
        color: theme.text,
        fontSize: { xs: '0.95rem', sm: '1.05rem' },
        mb: 1.5,
      }}
    >
      {text}
    </Typography>
  );

  // --- Skeleton helpers ---

  const renderSkeletonCards = () => (
    <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, overflowX: 'auto', pb: 1 }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <Paper key={i} sx={{ ...cardSx, minWidth: { xs: 140, sm: 160 }, flex: '0 0 auto' }}>
          <Skeleton variant="text" width={80} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
          <Skeleton variant="rounded" width={100} height={32} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0', mt: 0.5 }} />
        </Paper>
      ))}
    </Box>
  );

  // --- Summary Cards ---

  const cmp = data.comparison;
  const unrec = data.unrecordedRevenue || 0;
  const unrecActive = SHOW_UNRECORDED_FEATURE && includeUnrecorded;
  const unrecAdjust = unrecActive ? unrec : 0;

  // Effective values when toggle ON include unrecorded revenue
  const effectiveTotalIncome = data.totalIncome + unrecAdjust;
  const effectiveNetProfit = data.netProfit + unrecAdjust;
  const effectivePnlChart = (data.pnlChart || []).map((r) => ({
    ...r,
    income: (r.income || 0) + (unrecActive ? (r.unrecordedRevenue || 0) : 0),
  }));
  const effectiveIncomeByCategory = unrecActive && unrec > 0
    ? (() => {
        const denom = effectiveTotalIncome > 0 ? effectiveTotalIncome : 1;
        const scaled = data.incomeByCategory.map((c) => ({
          ...c,
          percent: Math.round(((c.amount || 0) / denom) * 1000) / 10,
        }));
        return [
          ...scaled,
          {
            category: 'Unrecorded (Card/Other)',
            amount: unrec,
            percent: Math.round((unrec / denom) * 1000) / 10,
          },
        ];
      })()
    : data.incomeByCategory;

  // KPI sparkline series — last 14 days of effective pnlChart
  const trailing = effectivePnlChart.slice(-14);
  const incomeSeries = trailing.map((r) => r.income || 0);
  const expenseSeries = trailing.map((r) => r.expenses || 0);
  const netSeries = trailing.map((r) => (r.income || 0) - (r.expenses || 0));

  const summaryCards = [
    {
      label: 'Total Income',
      value: effectiveTotalIncome,
      color: theme.success || '#4CAF50',
      previousValue: cmp?.previousIncome,
      invertColor: false,
      spark: incomeSeries,
    },
    {
      label: 'Total Expenses',
      value: data.totalExpenses,
      color: theme.danger || '#D99982',
      previousValue: cmp?.previousExpenses,
      invertColor: true, // lower expenses = good
      spark: expenseSeries,
    },
    { label: 'Non-Revenue Costs', value: data.nonRevenueCosts, color: theme.warning || '#FFC107', spark: [] as number[] },
    {
      label: 'Net Profit/Loss',
      value: effectiveNetProfit,
      color: effectiveNetProfit >= 0 ? (theme.success || '#4CAF50') : (theme.danger || '#D99982'),
      bold: true,
      previousValue: cmp?.previousNetProfit,
      spark: netSeries,
      invertColor: false,
    },
    ...(SHOW_UNRECORDED_FEATURE ? [{
      label: 'Unrecorded Revenue',
      value: unrec,
      color: includeUnrecorded ? (theme.gray?.[300] || '#BBB') : (theme.gray?.[600] || '#666'),
      dim: includeUnrecorded,
      spark: [] as number[],
      tooltip: includeUnrecorded
        ? 'Non-aggregator daily revenue (Card, etc). Included in totals + charts. VAT not applied. May overlap with manual "Card Settlement" income entries — toggle OFF if you already record settlements separately.'
        : 'Non-aggregator daily revenue (Card, etc). Excluded from totals. VAT not applied. CSV import pending.',
    }] : []),
  ];

  // --- Render ---

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, backgroundColor: theme.background, minHeight: '100vh', pb: { xs: 'calc(96px + env(safe-area-inset-bottom, 0px))', sm: 3 } }}>
      <FinanceTabBar />

      {/* Header — confident-shell hero band */}
      <PageHeroBand
        eyebrow={`Finance · ${(PERIOD_OPTIONS.find(o => o.key === period)?.label) || 'This Month'}`}
        title="Finance Dashboard"
        heroLabel="Net profit"
        heroValue={formatCurrency(effectiveNetProfit, { showDecimals: false })}
        actions={<Box sx={{ display: 'flex', gap: 1, flexShrink: 0,
          '& .MuiButton-root': { backgroundColor: 'color-mix(in srgb, var(--band-text) 10%, transparent)', color: 'var(--band-text)', borderColor: 'color-mix(in srgb, var(--band-text) 25%, transparent)', '&:hover': { backgroundColor: 'color-mix(in srgb, var(--band-text) 18%, transparent)', borderColor: 'color-mix(in srgb, var(--band-text) 40%, transparent)' } } }}>
          <Tooltip title="Export P&L">
            <SecondaryButton
              onClick={handleExportPnl}
              disabled={exporting}
              startIcon={<ArrowDownTrayIcon style={{ width: 18, height: 18 }} />}
              sx={{ fontSize: '0.813rem', px: 1.5 }}
            >
              Export
            </SecondaryButton>
          </Tooltip>
          <SecondaryButton
            onClick={handleRefresh}
            disabled={refreshing}
            startIcon={<ArrowPathIcon style={{ width: 18, height: 18 }} className={refreshing ? 'animate-spin' : ''} />}
            sx={{ fontSize: '0.813rem', px: 1.5 }}
          >
            Refresh
          </SecondaryButton>
        </Box>}
      />

      {/* Period Selector + Include-Unrecorded Toggle */}
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center', mb: period === 'CUSTOM' ? 1.5 : 0 }}>
          {PERIOD_OPTIONS.map((opt) => {
            const isActive = period === opt.key;
            return (
              <Chip
                key={opt.key}
                label={opt.label}
                onClick={() => setPeriod(opt.key)}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: 32,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  backgroundColor: isActive ? `${theme.primary}22` : theme.gray?.[100] || '#F0F0F0',
                  color: isActive ? theme.primary : theme.gray?.[500] || '#666',
                  border: isActive ? `1.5px solid ${theme.primary}` : `1px solid transparent`,
                  '&:hover': { backgroundColor: isActive ? `${theme.primary}30` : theme.gray?.[200] || '#E0E0E0' },
                }}
              />
            );
          })}
          {SHOW_UNRECORDED_FEATURE && unrec > 0 && (
            <Tooltip
              title="Adds non-aggregator daily revenue (Card, etc) into Total Income, Net Profit, and charts. Warning: may double-count if you record manual settlement income for the same channels."
              arrow
              placement="top"
            >
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={includeUnrecorded}
                    onChange={(e) => setIncludeUnrecorded(e.target.checked)}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: theme.gray?.[600] || '#666' }}>
                    Incl. unrecorded
                  </Typography>
                }
                sx={{ ml: 'auto', mr: 0 }}
              />
            </Tooltip>
          )}
        </Box>
        {period === 'CUSTOM' && (
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <FinanceDateRange
              from={customFrom}
              to={customTo}
              onFromChange={setCustomFrom}
              onToChange={setCustomTo}
              showLabels
              sx={{ flex: '1 1 260px' }}
            />
            <PrimaryButton
              size="small"
              onClick={() => fetchDashboard(true)}
              disabled={!customFrom || !customTo}
              sx={{
                borderRadius: '10px',
                minHeight: 40,
              }}
            >
              Apply
            </PrimaryButton>
          </Box>
        )}
      </Box>

      {/* Onboarding Wizard */}
      {showOnboarding && (
        <OnboardingWizard onComplete={() => setShowOnboarding(false)} />
      )}

      {/* Empty state — only show if onboarding was never completed */}
      {!loading && !hasData && !showOnboarding && !localStorage.getItem(ONBOARDING_KEY) ? (
        <EmptyState
          icon={<ChartBarIcon style={{ width: 44, height: 44, color: 'var(--text-tertiary)' }} />}
          title="No financial data yet"
          subtitle="Start tracking your expenses to see insights here"
        />
      ) : (
        <>
          {/* Summary Cards Row */}
          {loading ? renderSkeletonCards() : (
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 1, sm: 2 },
                mb: { xs: 2, sm: 3 },
                overflowX: { xs: 'auto', sm: 'visible' },
                flexWrap: { xs: 'nowrap', sm: 'wrap' },
                pb: { xs: 1, sm: 0 },
                // Hide scrollbar on mobile
                '&::-webkit-scrollbar': { display: 'none' },
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
                WebkitMaskImage: {
                  xs: 'linear-gradient(to right, black calc(100% - 32px), transparent 100%)',
                  sm: 'none',
                },
                maskImage: {
                  xs: 'linear-gradient(to right, black calc(100% - 32px), transparent 100%)',
                  sm: 'none',
                },
              }}
            >
              {summaryCards.map((card) => {
                const cardEl = (
                  <Paper
                    sx={{
                      ...cardSx,
                      minWidth: { xs: 140, sm: 'auto' },
                      flex: { xs: '0 0 auto', sm: 1 },
                      cursor: 'tooltip' in card && card.tooltip ? 'help' : 'default',
                      opacity: 'dim' in card && card.dim ? 0.45 : 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.gray?.[500] || '#666',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        fontSize: '0.65rem',
                        letterSpacing: '0.05em',
                      }}
                    >
                      {card.label}
                    </Typography>
                    <Typography
                      sx={{
                        // Honest zero state: no movement, no colour theatre —
                        // quiet neutral until there is something to say.
                        fontWeight: card.value === 0 ? 600 : card.bold ? 800 : 700,
                        color: card.value === 0 ? 'var(--text-tertiary)' : card.color,
                        fontSize: { xs: '1.35rem', sm: '1.75rem' },
                        fontVariantNumeric: 'tabular-nums',
                        letterSpacing: '-0.01em',
                        lineHeight: 1.15,
                        mt: 0.25,
                      }}
                    >
                      {formatCurrency(card.value, { showDecimals: false })}
                    </Typography>
                    {'previousValue' in card && card.previousValue != null && (
                      <Box sx={{ mt: 0.5 }}>
                        <ComparisonBadge
                          currentValue={card.value}
                          previousValue={card.previousValue}
                          invertColor={card.invertColor}
                        />
                      </Box>
                    )}
                    {card.value !== 0 && card.spark && card.spark.length >= 2 && card.spark.some((v: number) => v !== 0) && (
                      <Box sx={{ mt: 0.75, opacity: 0.85 }}>
                        <KpiSparkline values={card.spark} color={card.color} />
                      </Box>
                    )}
                  </Paper>
                );
                return 'tooltip' in card && card.tooltip ? (
                  <Tooltip key={card.label} title={card.tooltip} arrow placement="top">
                    {cardEl}
                  </Tooltip>
                ) : (
                  <React.Fragment key={card.label}>{cardEl}</React.Fragment>
                );
              })}
            </Box>
          )}

          {/* One-tap money verbs — dashboard Quick Actions (Wave A) */}
          {!loading && <QuickActions />}

          {/* Tip: non-aggregator unrecorded revenue not in totals */}
          {!loading && unrec > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                p: { xs: 1, sm: 1.25 },
                mb: { xs: 2, sm: 3 },
                borderRadius: '10px',
                backgroundColor: theme.gray?.[100] || '#F5F3EF',
                border: `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
              }}
            >
              <InformationCircleIcon
                style={{ width: 18, height: 18, flexShrink: 0, color: theme.gray?.[500] || '#666', marginTop: 2 }}
              />
              <Typography sx={{ fontSize: '0.78rem', color: theme.gray?.[600] || '#555', lineHeight: 1.4 }}>
                {formatCurrency(unrec, { showDecimals: false })} of non-aggregator revenue
                (Card, Bank Transfer etc.) is <strong>not included</strong> in Total Income or
                Net Profit. These channels record revenue separately via bank/UPI settlement
                imports — including them here would double-count.
              </Typography>
            </Box>
          )}

          {/* === Collapsible chart suite === */}
          {!loading && (
            <>
              {/* 0. Insights — auto-detected patterns (top of feed) */}
              <InsightsList defaultExpanded />

              {/* 1. Income vs Expense — grouped bars + margin overlay + D/W/M */}
              <CollapsibleChartSection
                title="Income vs Expenses"
                subtitle="Grouped bars with margin overlay · Daily / Weekly / Monthly"
                defaultExpanded
              >
                {() => <IncomeExpenseBarChart data={effectivePnlChart} loading={loading} />}
              </CollapsibleChartSection>

              {/* 2. Income Breakdown — always rendered so the section is discoverable */}
              <CollapsibleChartSection
                title="Income Breakdown"
                subtitle={
                  data.incomeByCategory.length > 0
                    ? 'By income category · click slice to drill down'
                    : 'No income categorized in this period'
                }
                defaultExpanded
              >
                {() => (
                  <ExpenseCategoryPie
                    data={effectiveIncomeByCategory}
                    loading={loading}
                    label="income"
                    dateFrom={activeDateFrom}
                    dateTo={activeDateTo}
                  />
                )}
              </CollapsibleChartSection>

              {/* 3. Expense Breakdown */}
              <CollapsibleChartSection
                title="Expense Breakdown"
                subtitle="By expense category · click slice to drill down"
                defaultExpanded
              >
                {() => (
                  <ExpenseCategoryPie
                    data={data.expensesByCategory}
                    loading={loading}
                    label="expense"
                    dateFrom={activeDateFrom}
                    dateTo={activeDateTo}
                  />
                )}
              </CollapsibleChartSection>

              {/* 4. Channel revenue stacked over time */}
              <CollapsibleChartSection
                title="Channel Revenue"
                subtitle="Stacked by channel · which channels grow / shrink"
                defaultExpanded
              >
                {() => <ChannelStackedBarChart dateFrom={activeDateFrom} dateTo={activeDateTo} />}
              </CollapsibleChartSection>

              {/* 5. Top expense payees (collapsed — fetch on expand) */}
              <CollapsibleChartSection
                title="Top Payees"
                subtitle="Where money actually flows · top 10 by total spend"
              >
                {() => <TopPayeesChart dateFrom={activeDateFrom} dateTo={activeDateTo} />}
              </CollapsibleChartSection>

              {/* 6. Day-of-Week heatmap (collapsed) */}
              <CollapsibleChartSection
                title="Day-of-Week Heatmap"
                subtitle="Revenue rhythm · Mon–Sun × weeks"
              >
                {() => <DayOfWeekHeatmap data={effectivePnlChart} metric="income" />}
              </CollapsibleChartSection>

              {/* 7. Cashflow waterfall (collapsed) */}
              <CollapsibleChartSection
                title="Cashflow Waterfall"
                subtitle="Period story: income → expenses → net"
              >
                {() => (
                  <CashflowWaterfall
                    totalIncome={effectiveTotalIncome}
                    expensesByCategory={data.expensesByCategory}
                    nonRevenueCosts={data.nonRevenueCosts}
                  />
                )}
              </CollapsibleChartSection>

            </>
          )}

          {/* Reports cross-link */}
          {!loading && hasData && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1, mb: { xs: 2, sm: 3 } }}>
              <Button
                size="small"
                endIcon={<ArrowTopRightOnSquareIcon style={{ width: 14, height: 14 }} />}
                onClick={() => navigate('/vendor/reports')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  color: theme.primary,
                  borderRadius: '10px',
                  minHeight: 44,
                  '&:hover': { backgroundColor: `${theme.primary}08` },
                  '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: '2px' },
                }}
              >
                View in Reports
              </Button>
            </Box>
          )}

          {/* Payables Mini-Summary */}
          {!loading && (data.payablesSummary.totalOutstanding > 0 || data.payablesSummary.overdueAmount > 0) && (
            <Paper sx={{ ...cardSx, mb: { xs: 2, sm: 3 }, p: { xs: 2, sm: 2.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                {sectionTitle('Supplier Payables')}
                <Button
                  size="small"
                  endIcon={<ArrowTopRightOnSquareIcon style={{ width: 14, height: 14 }} />}
                  onClick={() => navigate('/vendor/finance/payables')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    color: theme.primary,
                    minHeight: 44,
                    '&:hover': { backgroundColor: `${theme.primary}08` },
                    '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: '2px' },
                  }}
                >
                  View All
                </Button>
              </Box>

              <Box sx={{ display: 'flex', gap: { xs: 2, sm: 4 }, mb: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Total Outstanding
                  </Typography>
                  <Typography sx={{ fontWeight: 700, color: theme.danger || '#D99982', fontSize: '1.1rem', lineHeight: 1.2, mt: 0.25 }}>
                    {formatCurrency(data.payablesSummary.totalOutstanding, { showDecimals: false })}
                  </Typography>
                </Box>
                {data.payablesSummary.overdueAmount > 0 && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ExclamationTriangleIcon style={{ width: 12, height: 12, color: theme.warning || '#FFC107' }} />
                      <Typography variant="caption" sx={{ color: theme.warning || '#FFC107', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Overdue
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: theme.warning || '#FFC107', fontSize: '1.1rem', lineHeight: 1.2, mt: 0.25 }}>
                      {formatCurrency(data.payablesSummary.overdueAmount, { showDecimals: false })}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Top 3 suppliers */}
              {data.payablesSummary.topSuppliers.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {data.payablesSummary.topSuppliers.slice(0, 3).map((supplier, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 0.75,
                        px: 1,
                        borderRadius: '8px',
                        backgroundColor: `${theme.primary}06`,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: theme.text, fontWeight: 500, fontSize: '0.813rem' }}>
                        {supplier.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.danger || '#D99982', fontWeight: 700, fontSize: '0.875rem' }}>
                        {formatCurrency(supplier.outstanding, { showDecimals: false })}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          )}

          {/* Non-Revenue Costs Summary */}
          {!loading && data.nonRevenueCosts > 0 && (
            <Paper sx={{ ...cardSx, p: { xs: 2, sm: 2.5 }, mb: { xs: 2, sm: 3 } }}>
              {sectionTitle('Non-Revenue Costs')}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ fontWeight: 700, color: theme.warning || '#FFC107', fontSize: '1.25rem' }}>
                  {formatCurrency(data.nonRevenueCosts, { showDecimals: false })}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.813rem' }}>
                  Voids + Complimentary + Staff Meals
                </Typography>
              </Box>
            </Paper>
          )}

          {/* Tax Summary */}
          {!loading && data.taxSummary && (data.taxSummary.cgst > 0 || data.taxSummary.sgst > 0 || data.taxSummary.igst > 0) && (
            <Paper sx={{ ...cardSx, p: { xs: 2, sm: 2.5 }, mb: { xs: 2, sm: 3 } }}>
              {sectionTitle('Tax Summary')}
              <Box sx={{ display: 'flex', gap: { xs: 2, sm: 4 }, flexWrap: 'wrap' }}>
                {data.taxSummary.cgst > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {taxSublabels[0] || 'CGST'}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: theme.text, fontSize: '1.1rem', lineHeight: 1.2, mt: 0.25 }}>
                      {formatCurrency(data.taxSummary.cgst, { showDecimals: false })}
                    </Typography>
                  </Box>
                )}
                {data.taxSummary.sgst > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {taxSublabels[1] || 'SGST'}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: theme.text, fontSize: '1.1rem', lineHeight: 1.2, mt: 0.25 }}>
                      {formatCurrency(data.taxSummary.sgst, { showDecimals: false })}
                    </Typography>
                  </Box>
                )}
                {data.taxSummary.igst > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {taxSublabels[2] || 'IGST'}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: theme.text, fontSize: '1.1rem', lineHeight: 1.2, mt: 0.25 }}>
                      {formatCurrency(data.taxSummary.igst, { showDecimals: false })}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          )}

          {/* Budget Progress */}
          {!loading && data.budgetProgress && data.budgetProgress.length > 0 && (
            <Paper sx={{ ...cardSx, p: { xs: 2, sm: 2.5 }, mb: { xs: 2, sm: 3 } }}>
              {sectionTitle('Budget Progress')}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {data.budgetProgress.map((bp) => {
                  const pct = Math.min(bp.percent, 120); // cap visual at 120%
                  const barColor = bp.percent >= 100
                    ? (theme.danger || '#D99982')
                    : bp.percent >= 80
                      ? (theme.warning || '#FF9800')
                      : (theme.success || '#4CAF50');
                  return (
                    <Box key={bp.categoryName}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.813rem', fontWeight: 600, color: theme.text }}>
                          {bp.categoryName}
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: barColor }}>
                          {formatCurrency(bp.spentAmount, { showDecimals: false })} / {formatCurrency(bp.budgetAmount, { showDecimals: false })}
                          <span style={{ marginLeft: 4, fontSize: '0.65rem' }}>({Math.round(bp.percent)}%)</span>
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: theme.gray?.[200] || '#E8E5E0',
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${Math.min(pct, 100)}%`,
                            height: '100%',
                            borderRadius: 3,
                            backgroundColor: barColor,
                            transition: 'width 0.4s ease',
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          )}

          {/* Fixed Costs + Expected Deposits row */}
          {!loading && ((data.fixedCosts && data.fixedCosts > 0) || (data.expectedDeposits && data.expectedDeposits.count > 0)) && (
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 1, sm: 2 },
                mb: { xs: 2, sm: 3 },
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
              }}
            >
              {data.fixedCosts != null && data.fixedCosts > 0 && (
                <Paper sx={{ ...cardSx, flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.gray?.[500] || '#666',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      fontSize: '0.65rem',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Monthly Fixed Costs
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: theme.text,
                      fontSize: { xs: '1.1rem', sm: '1.35rem' },
                      lineHeight: 1.2,
                      mt: 0.25,
                    }}
                  >
                    {formatCurrency(data.fixedCosts, { showDecimals: false })}
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.gray?.[400] || '#999', fontSize: '0.65rem', mt: 0.5 }}>
                    Recurring expense obligations
                  </Typography>
                </Paper>
              )}
              {data.expectedDeposits && data.expectedDeposits.count > 0 && (
                <Paper sx={{ ...cardSx, flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.gray?.[500] || '#666',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      fontSize: '0.65rem',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Expected Deposits
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: theme.success || '#4CAF50',
                      fontSize: { xs: '1.1rem', sm: '1.35rem' },
                      lineHeight: 1.2,
                      mt: 0.25,
                    }}
                  >
                    {formatCurrency(data.expectedDeposits.total, { showDecimals: false })}
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.gray?.[400] || '#999', fontSize: '0.65rem', mt: 0.5 }}>
                    {data.expectedDeposits.count} pending settlement{data.expectedDeposits.count !== 1 ? 's' : ''}
                  </Typography>
                </Paper>
              )}
            </Box>
          )}

          {/* Food Cost Gauge */}
          {!loading && (data.foodCostPercent > 0 || data.foodCostTarget > 0) && (
            <Paper sx={{ ...cardSx, mb: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center', py: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {sectionTitle('Food Cost %')}
                {cmp?.previousFoodCostPercent != null && (
                  <ComparisonBadge
                    currentValue={data.foodCostPercent}
                    previousValue={cmp.previousFoodCostPercent}
                    invertColor
                  />
                )}
              </Box>
              <FoodCostGauge value={data.foodCostPercent} target={data.foodCostTarget} />
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default FinanceDashboard;
